/* VORYNEX "Build your stack" — see sections/vpx-stack.liquid.
 * The list, the summary and the add-to-cart work on their own; three.js loads after the page has loaded and
 * renders only while something moves (a drop, a slide, a drag, a short idle sway), never off-screen or in a
 * hidden tab. The 3D products stand on a photo of a podium: the camera is calibrated to the podium in the image
 * and the image box drives the camera's view offset, so photo and 3D stay locked together at any size. */

const root = document.querySelector('[data-vpx-stack]');
if (root) init(root);

function init(root) {
  const cfg = JSON.parse(root.querySelector('[data-vpx-stack-config]').textContent);
  const S = cfg.strings;
  const MAX_QTY = cfg.maxQty || 5;
  const stage = root.querySelector('[data-vpx-stack-stage]');
  const backdrop = root.querySelector('[data-vpx-stack-backdrop]');
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cartRoot = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || cfg.root || '/').replace(/\/?$/, '/');

  // ---------- Money, in the store's own format (same as the header's mini-cart) ----------
  function money(cents) {
    const delimit = (amount, precision, thousands, decimal) => {
      const parts = (amount / 100).toFixed(precision).split('.');
      return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (parts[1] ? decimal + parts[1] : '');
    };
    const value = Number(cents) || 0;
    return (cfg.moneyFormat || '€{{amount_with_comma_separator}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, p) => {
      switch (p) {
        case 'amount_no_decimals': return delimit(value, 0, ',', '.');
        case 'amount_with_comma_separator': return delimit(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return delimit(value, 2, "'", '.');
        case 'amount_with_space_separator': return delimit(value, 2, ' ', ',');
        default: return delimit(value, 2, ',', '.');
      }
    });
  }
  // One-time or every month: a product with its own subscription plan (Seal) costs its plan price every month;
  // one without a plan stays a one-time purchase at its price.
  let monthly = false;
  const priceOf = it => (monthly && it.plan ? it.planPrice : it.price);
  // Per-serving prices are rounded to the cent as shown; the daily figure adds those same values, so they add up.
  const perServing = it => Math.round(priceOf(it) / it.servings);
  const plural = n => (n === 1 ? S.one : S.other.replace('[count]', n));
  const servingsText = n => (n === 1 ? S.servingsOne : S.servingsOther.replace('[count]', n));

  // ---------- Rows (one per product, or one per flavour group), each with a quantity ----------
  const rows = [...root.querySelectorAll('[data-vpx-stack-row]')].map(el => {
    const key = el.dataset.vpxStackRow;
    const items = cfg.items.filter(i => i.row === key);
    const radio = el.querySelector('[data-vpx-stack-flavour]:checked');
    const current = (radio && items.find(i => i.block === radio.value)) || items.find(i => i.available) || items[0];
    return { key, el, items, current, qty: 1, toggle: el.querySelector('[data-vpx-stack-toggle]') };
  }).filter(r => r.items.length);
  const isOn = r => r.toggle.checked && r.current.available;
  const chosen = () => rows.filter(isOn).map(r => ({ ...r.current, qty: r.qty, row: r.key }));

  function showRow(r) {
    const it = r.current;
    r.el.classList.toggle('is-on', r.toggle.checked);
    r.el.querySelector('[data-vpx-stack-price]').textContent = money(priceOf(it) * r.qty);
    r.el.querySelector('[data-vpx-stack-size]').textContent =
      (it.size ? it.size + ' · ' : '') + (r.qty > 1 ? r.qty + ' × ' : '') + servingsText(it.servings);
    r.el.querySelector('[data-vpx-stack-each]').textContent = S.perServing.replace('[amount]', money(perServing(it)));
    r.el.querySelector('[data-vpx-stack-qty]').textContent = r.qty;
    const [less, more] = r.el.querySelectorAll('[data-vpx-stack-step]');
    less.disabled = r.qty <= 1; more.disabled = r.qty >= MAX_QTY;
    const thumb = r.el.querySelector('[data-vpx-stack-thumb]');
    if (thumb && thumb.getAttribute('src') !== it.thumb) thumb.src = it.thumb;
    const radio = r.el.querySelector(`[data-vpx-stack-flavour][value="${it.block}"]`);
    if (radio && !radio.checked) radio.checked = true;
  }
  function setFlavour(r, it) { if (it && it.available) r.current = it; }

  // ---------- Opening links: ?add=<handle> (product pages), ?s=<variant>.<qty>,…&n=<name> (shared stacks) ----------
  let stackName = '';
  const params = new URLSearchParams(location.search);
  function applySelection(list) {      // list: [{ item, qty }]
    rows.forEach(r => { r.toggle.checked = false; r.qty = 1; });
    for (const { item, qty } of list) {
      const r = rows.find(x => x.items.includes(item));
      if (!r || !item.available) continue;
      setFlavour(r, item); r.toggle.checked = true; r.qty = Math.max(1, Math.min(MAX_QTY, qty || 1));
    }
    rows.forEach(showRow);
  }
  if (params.get('s')) {
    const list = params.get('s').split(',').map(p => {
      const [variant, qty] = p.split('.');
      return { item: cfg.items.find(i => String(i.variant) === variant), qty: Number(qty) };
    }).filter(x => x.item);
    if (list.length) applySelection(list);
    stackName = (params.get('n') || '').replace(/[<>]/g, '').trim().slice(0, 28);
  } else if (params.get('add')) {
    const item = cfg.items.find(i => i.handle === params.get('add'));
    if (item && item.available) applySelection([{ item, qty: 1 }]);
  }

  // ---------- Gift links: ?gift=<base64url JSON {i: id, f: from, t: to, m: message, s: "variant.qty,…"}> ----------
  // Only names, the message and the products travel in the link — no address, no prices. Anyone can make such a
  // link, so the message is shown as plain text, without web addresses, under a "written by the sender" note.
  const b64u = str => { let bin = ''; new TextEncoder().encode(str).forEach(b => { bin += String.fromCharCode(b); }); return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); };
  const unb64u = str => new TextDecoder().decode(Uint8Array.from(atob(str.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)));
  const clean = (s, max) => String(s || '').replace(/\b(?:https?:\/\/|www\.)\S+/gi, '').replace(/[<>]/g, '').replace(/\s+/g, ' ').trim().slice(0, max);
  let gift = null, giftOpened = false;
  try {
    const raw = params.get('gift');
    if (raw) {
      const g = JSON.parse(unb64u(raw));
      const list = String(g.s || '').split(',').map(p => { const [variant, qty] = p.split('.'); return { item: cfg.items.find(i => String(i.variant) === variant), qty: Number(qty) }; }).filter(x => x.item);
      if (list.length && g.f && g.t) gift = { id: clean(g.i, 12), from: clean(g.f, 30), to: clean(g.t, 30), msg: clean(g.m, 180), list };
    }
  } catch (e) { gift = null; }
  let sound = null, soundOn = true;             // the unboxing's sound: { ctx, handle } once the box is tapped
  if (gift) {
    root.classList.add('is-gift');
    // The stage fills the first screen below the site header, so the button is there without scrolling.
    const giftTop = () => root.style.setProperty('--vpx-gift-top', Math.max(0, Math.round(root.getBoundingClientRect().top + scrollY)) + 'px');
    giftTop(); addEventListener('resize', giftTop);
    applySelection(gift.list.map(x => ({ item: x.item, qty: x.qty })));
    const reveal = root.querySelector('[data-vpx-gift-reveal]');
    reveal.hidden = false;
    // Sound on by default (it only ever starts with the tap on the button); the choice is remembered.
    const soundBtn = reveal.querySelector('[data-vpx-gift-sound]');
    try { soundOn = localStorage.getItem('vpx-gift-sound') !== 'off'; } catch (e) { /* storage blocked: default */ }
    const showSound = () => soundBtn.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
    showSound();
    soundBtn.addEventListener('click', () => {
      soundOn = !soundOn; showSound();
      try { localStorage.setItem('vpx-gift-sound', soundOn ? 'on' : 'off'); } catch (e) { /* not remembered */ }
      if (sound && sound.handle) sound.handle.setMuted(!soundOn);
    });
    reveal.querySelector('[data-vpx-gift-from-text]').textContent = S.giftFrom.replace('[name]', gift.from);
    reveal.querySelector('[data-vpx-gift-to-text]').textContent = S.giftTo.replace('[name]', gift.to);
    reveal.querySelector('[data-vpx-gift-msg-text]').textContent = gift.msg;
    reveal.querySelector('[data-vpx-gift-msg-text]').hidden = !gift.msg;
    reveal.querySelector('[data-vpx-gift-sig]').textContent = '— ' + gift.from;
    const ul = reveal.querySelector('[data-vpx-gift-items]');
    gift.list.forEach(x => { const li = document.createElement('li'); li.textContent = x.item.name + (x.item.flavour ? ' · ' + x.item.flavour : '') + (x.qty > 1 ? ' ×' + x.qty : ''); ul.appendChild(li); });
    const own = new URL(location.href); own.searchParams.delete('gift');
    reveal.querySelector('[data-vpx-gift-own]').href = own.pathname + own.search;
    // The card is laid out now, while the page loads — invisible (visibility: hidden keeps it out of reach of the
    // keyboard and screen readers until it comes in): laying out its text, the emoji's font included, at the tap
    // held up the first frames of the unboxing (~60 ms measured).
    const card = reveal.querySelector('[data-vpx-gift-card]');
    card.hidden = false;
    const openBtn = reveal.querySelector('[data-vpx-gift-open]');
    const wait = ms => new Promise(r => setTimeout(r, ms));
    openBtn.addEventListener('click', async () => {
      if (openBtn.disabled) return;
      openBtn.disabled = true;
      // Browsers let sound start only from the tap itself: its audio context is created right here.
      let resumed = null;
      if (soundOn) {
        const AC = window.AudioContext || window.webkitAudioContext;
        try { if (AC) { sound = { ctx: new AC({ latencyHint: 'interactive' }), handle: null }; resumed = Promise.resolve(sound.ctx.resume && sound.ctx.resume()); } }
        catch (e) { sound = null; }
      }
      // Tapped while the 3D is still on its way (a slow connection): a few seconds' patience keeps the unboxing.
      if (!three && threeReady) {
        openBtn.classList.add('is-waiting'); openBtn.setAttribute('aria-busy', 'true');
        await Promise.race([threeReady, wait(4000)]);
        openBtn.classList.remove('is-waiting'); openBtn.removeAttribute('aria-busy');
      }
      giftOpened = true;
      reveal.querySelector('[data-vpx-gift-intro]').classList.add('is-leaving');
      // The audio device can take a moment to wake (Bluetooth headphones especially) while the intro fades; if it
      // doesn't wake in time, the unboxing plays silent rather than out of step with its sound.
      if (resumed) await Promise.race([resumed.catch(() => {}), wait(800)]);
      // The message never waits long on the 3D: without it (still loading, or no WebGL) the card comes at once, and
      // should the animation stall (a phone short of memory can lose its WebGL context) it comes after 6 s anyway.
      await (three ? Promise.race([three.openGift(sound), wait(6000)]) : wait(300));
      if (sound && !sound.handle) { Promise.resolve(sound.ctx.close && sound.ctx.close()).catch(() => {}); sound = null; }   // nothing played
      reveal.querySelector('[data-vpx-gift-intro]').hidden = true;
      requestAnimationFrame(() => { reveal.classList.add('is-open'); card.classList.add('is-in'); card.focus({ preventScroll: true }); });
    });
  }

  // ---------- Events: toggles, flavours, quantities, ready stacks ----------
  root.querySelector('[data-vpx-stack-picks]').addEventListener('change', e => {
    const row = rows.find(r => r.el.contains(e.target));
    if (!row) return;
    if (e.target.matches('[data-vpx-stack-flavour]')) {
      setFlavour(row, row.items.find(i => i.block === e.target.value));
      if (!row.toggle.checked && !row.toggle.disabled) row.toggle.checked = true;   // picking a flavour picks the product
      if (three) three.flavour(row.key);
    }
    changed(row);
  });
  root.querySelector('[data-vpx-stack-picks]').addEventListener('click', e => {
    const step = e.target.closest('[data-vpx-stack-step]');
    if (!step) return;
    const row = rows.find(r => r.el.contains(step));
    row.qty = Math.max(1, Math.min(MAX_QTY, row.qty + Number(step.dataset.vpxStackStep)));
    changed(row);
  });
  const presets = [...root.querySelectorAll('[data-vpx-stack-preset]')];
  presets.forEach(btn => btn.addEventListener('click', () => {
    const list = btn.dataset.vpxStackPreset.split(',').map(h => ({ item: cfg.items.find(i => i.handle === h), qty: 1 })).filter(x => x.item);
    applySelection(list);
    changed(null);
  }));
  function markPreset() {
    const now = chosen().map(i => i.handle + '.' + i.qty).sort().join(',');
    presets.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.vpxStackPreset.split(',').filter(h => cfg.items.some(i => i.handle === h && i.available)).map(h => h + '.1').sort().join(',') === now)));
  }
  function changed(row) {
    if (row) showRow(row);
    clearStatus();
    summary();
    if (three) three.sync();
  }

  // ---------- Summary and goals ----------
  // Goals count what the cart will hold after adding the stack: the cart as it is, minus what of the stack is
  // already in it, plus the stack. Never more than the real order, so an "unlocked" line is never premature.
  let cart = null;
  function othersInCart(list) {
    if (!cart) return 0;
    let others = cart.total_price;
    for (const line of cart.items) {
      const it = list.find(i => i.variant === line.variant_id);
      if (it) others -= line.final_price * Math.min(line.quantity, it.qty);
    }
    return Math.max(0, others);
  }
  function summary() {
    buySummary();
    const list = chosen();
    const total = list.reduce((s, i) => s + priceOf(i) * i.qty, 0), daily = list.reduce((s, i) => s + perServing(i), 0);
    const cartOthers = othersInCart(list);
    root.querySelectorAll('[data-vpx-stack-n], [data-vpx-stack-count]').forEach(el => { el.textContent = plural(list.length); });
    root.querySelectorAll('[data-vpx-stack-total]').forEach(el => { el.textContent = money(total); });
    const perDay = root.querySelector('[data-vpx-stack-perday]');
    perDay.hidden = !cfg.perDay && list.length > 0;
    perDay.textContent = list.length ? S.perDay.replace('[amount]', money(daily)) : S.pickOne;
    const projected = cartOthers + total, goals = [];
    // Free shipping is promised for a subscription only once the store's discount covers subscriptions (checked
    // 2026-09-28: it doesn't) — never more than checkout will give.
    if (cfg.shipGoal > 0 && !(monthly && !cfg.subFreeShipping)) goals.push({ at: cfg.shipGoal, done: S.shipDone, todo: S.shipTodo });
    if (cfg.giftGoal > 0 && !monthly) goals.push({ at: cfg.giftGoal, done: S.giftDone, todo: S.giftTodo });
    const tick = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
    const box = root.querySelector('[data-vpx-stack-goals]');
    box.textContent = '';
    for (const g of goals) {
      const done = projected >= g.at, el = document.createElement('div');
      el.className = 'vpx-goal' + (done ? ' is-done' : '');
      el.innerHTML = `<div class="vpx-goal__text">${done ? tick : ''}<span></span></div><div class="vpx-goal__bar"><i></i></div>`;
      el.querySelector('span').textContent = done ? g.done : g.todo.replace('[amount]', money(g.at - projected));
      el.querySelector('i').style.setProperty('--p', Math.min(1, projected / g.at).toFixed(3));
      box.appendChild(el);
    }
    const inCart = root.querySelector('[data-vpx-stack-incart]');
    inCart.hidden = !(cartOthers > 0 && goals.length);
    if (!inCart.hidden) inCart.textContent = S.inCart.replace('[amount]', money(cartOthers));
    addBtn.disabled = busy || !list.length;
    root.querySelector('[data-vpx-stack-empty]').hidden = !!list.length;
    const stageName = root.querySelector('[data-vpx-stack-stage-name]');
    stageName.hidden = !stackName; stageName.textContent = stackName ? `«${stackName}» · ` : '';
    if (shareBox) shareBox.querySelectorAll('button').forEach(b => { b.disabled = !list.length; });
    markPreset();
  }
  async function readCart() {
    try { cart = await (await fetch(cartRoot + 'cart.js', { headers: { Accept: 'application/json' } })).json(); }
    catch (e) { cart = null; }
    summary();
  }

  // ---------- One-time or every month ----------
  // One-time is the default. Every month shows its terms and needs a tick; products without a plan are named and
  // go in once; a cart marked as a gift can't take a subscription (it would renew to the friend every month).
  const buyBox = root.querySelector('[data-vpx-stack-buy]');
  const subInfo = buyBox && buyBox.querySelector('[data-vpx-sub-info]');
  const consent = buyBox && buyBox.querySelector('[data-vpx-sub-consent]');
  const consentError = buyBox && buyBox.querySelector('[data-vpx-sub-error]');
  const cartIsGift = () => !!(cart && cart.attributes && String(cart.attributes.Gift || '').toLowerCase() === 'yes');
  if (buyBox) buyBox.addEventListener('change', e => {
    if (e.target.matches('[data-vpx-buy]')) { monthly = e.target.value === 'monthly'; consentError.hidden = true; rows.forEach(showRow); changed(null); }
    else if (e.target === consent && consent.checked) { consentError.hidden = true; clearStatus(); }
  });
  function buySummary() {
    if (!buyBox) return;
    const list = chosen(), withPlan = list.filter(i => i.plan), without = list.filter(i => !i.plan);
    const sum = (arr, f) => arr.reduce((s, i) => s + f(i) * i.qty, 0);
    const can = withPlan.length > 0;
    const monthlyRadio = buyBox.querySelector('[data-vpx-buy][value="monthly"]');
    monthlyRadio.disabled = !can;
    if (!can && monthly) {                            // nothing left that has a plan: back to one-time
      monthly = false; buyBox.querySelector('[data-vpx-buy][value="once"]').checked = true; rows.forEach(showRow);
    }
    const today = sum(list, i => (i.plan ? i.planPrice : i.price)), recurring = sum(withPlan, i => i.planPrice);
    const pct = can ? Math.round(100 * (1 - recurring / sum(withPlan, i => i.price))) : 0;
    buyBox.querySelector('[data-vpx-buy-price="once"]').textContent = list.length ? money(sum(list, i => i.price)) : '';
    buyBox.querySelector('[data-vpx-buy-price="monthly"]').textContent = can ? money(today) : '';
    buyBox.querySelector('[data-vpx-buy-save]').textContent = pct > 0 ? S.subSave.replace('[percent]', pct) : '';
    const note = buyBox.querySelector('[data-vpx-buy-note]');
    note.hidden = !list.length || (can && !without.length);
    note.textContent = can ? S.subOnceItems.replace('[names]', without.map(i => i.name).join(', ')) : S.subNone;
    subInfo.hidden = !monthly;
    root.classList.toggle('is-monthly', monthly);
    const warn = buyBox.querySelector('[data-vpx-sub-warn]');
    warn.hidden = !(monthly && cartIsGift());
    warn.textContent = warn.hidden ? '' : S.subCartIsGift;
    const rec = root.querySelector('[data-vpx-stack-recurring]');
    rec.hidden = !(monthly && can);
    rec.textContent = rec.hidden ? '' : without.length ? S.subThen.replace('[amount]', money(recurring)) : S.subPerMonth.replace('[amount]', money(recurring));
    const giftOnce = root.querySelector('[data-vpx-gift-once]');
    if (giftOnce) giftOnce.hidden = !monthly;
  }
  // Before a monthly add: the tick, and no gift cart. Says why next to the button and at the tick itself.
  function subscriptionOk() {
    if (!monthly || !buyBox) return true;
    const reason = cartIsGift() ? S.subCartIsGift : consent.checked ? '' : S.subConsentError;
    if (!reason) return true;
    consentError.textContent = reason; consentError.hidden = false;
    root.classList.add('is-error'); status.textContent = reason;
    consent.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    consent.focus({ preventScroll: true });
    return false;
  }

  // ---------- Add the stack to the cart ----------
  // One request for the whole stack; the button is locked while it runs, so a double tap can't add twice. The
  // header's mini-cart listens for cart:refresh (badge + dropdown), as with the Spotlight.
  const addBtn = root.querySelector('[data-vpx-stack-add]');
  const addLabel = root.querySelector('[data-vpx-stack-add-label]');
  const status = root.querySelector('[data-vpx-stack-status]');
  let busy = false, resetTimer = 0;
  const clearStatus = () => {
    if (busy) return;
    status.textContent = ''; root.classList.remove('is-added', 'is-error');
    if (consentError) consentError.hidden = true;   // a refused add's reason goes once the stack changes
  };
  addBtn.addEventListener('click', async () => {
    const list = chosen();
    if (busy || !list.length) return;
    if (!subscriptionOk()) return;
    busy = true; clearTimeout(resetTimer);
    addBtn.disabled = true; addBtn.setAttribute('aria-busy', 'true'); root.classList.remove('is-added', 'is-error'); root.classList.add('is-adding');
    addLabel.textContent = S.adding; status.textContent = '';
    try {
      // Every month: each product with a plan goes in on it (Seal takes over at checkout); the rest go in once.
      const items = list.map(i => Object.assign({ id: i.variant, quantity: i.qty }, monthly && i.plan ? { selling_plan: i.plan } : {}));
      const res = await fetch(cartRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await res.json().catch(() => ({}));
      // Shopify answers 422 with a readable reason (e.g. stock); nothing of the stack is added then.
      if (!res.ok) throw new Error(data.description || data.message || S.error);
      root.classList.add('is-added'); addLabel.textContent = S.added; status.textContent = S.addedStatus;
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      openMiniCart();
      readCart();
      resetTimer = setTimeout(() => { root.classList.remove('is-added'); addLabel.textContent = S.add; status.textContent = ''; }, 2600);
    } catch (e) {
      root.classList.add('is-error'); addLabel.textContent = S.add;
      status.textContent = e && e.message && e.message !== 'Failed to fetch' ? e.message : S.error;
    } finally {
      busy = false; root.classList.remove('is-adding'); addBtn.removeAttribute('aria-busy');
      addBtn.disabled = !chosen().length;
    }
  });
  function openMiniCart() {
    const trigger = document.querySelector('[class*="ai-vorynex-header__cart-trigger"]');
    const panel = document.querySelector('[class*="ai-vorynex-header__cart-dropdown"]');
    if (!trigger || (panel && panel.classList.contains('active'))) return;
    const r = trigger.getBoundingClientRect();
    if (r.bottom > 0 && r.top < innerHeight) setTimeout(() => trigger.click(), 60);
  }

  // ---------- Name and share: a link that reopens this stack, and an Instagram-Story image of it ----------
  const shareBox = root.querySelector('[data-vpx-stack-share]');
  if (shareBox) {
    const nameInput = shareBox.querySelector('[data-vpx-stack-name]');
    const shareStatus = shareBox.querySelector('[data-vpx-stack-share-status]');
    if (stackName) nameInput.value = stackName;
    nameInput.addEventListener('input', () => { stackName = nameInput.value.replace(/[<>]/g, '').trim().slice(0, 28); summary(); });
    const say = t => { shareStatus.textContent = t; };
    function shareLink() {
      const url = new URL(location.origin + location.pathname);
      const view = params.get('view'); if (view) url.searchParams.set('view', view);
      // A draft theme stays open for whoever tests the link (the preview isn't in the address bar).
      if (window.Shopify && Shopify.theme && Shopify.theme.role !== 'main' && Shopify.theme.id) url.searchParams.set('preview_theme_id', Shopify.theme.id);
      url.searchParams.set('s', chosen().map(i => i.variant + '.' + i.qty).join(','));
      if (stackName) url.searchParams.set('n', stackName);
      url.searchParams.set('utm_source', 'stack_share'); url.searchParams.set('utm_medium', 'social'); url.searchParams.set('utm_campaign', 'build_your_stack');
      return url.toString();
    }
    async function copy(text) {
      try { await navigator.clipboard.writeText(text); return true; }
      catch (e) {
        const ta = Object.assign(document.createElement('textarea'), { value: text }); ta.style.cssText = 'position:fixed;opacity:0';
        document.body.appendChild(ta); ta.select(); let ok = false; try { ok = document.execCommand('copy'); } catch (err) { /* nothing */ }
        ta.remove(); return ok;
      }
    }
    shareBox.querySelector('[data-vpx-stack-copy]').addEventListener('click', async () => { if (await copy(shareLink())) say(S.copied); });
    const shareBtn = shareBox.querySelector('[data-vpx-stack-share-btn]');
    shareBtn.addEventListener('click', async () => {
      if (!chosen().length || shareBtn.getAttribute('aria-busy')) return;
      shareBtn.setAttribute('aria-busy', 'true'); say(S.preparing);
      const link = shareLink();
      try {
        const blob = await storyImage();
        const file = new File([blob], (stackName || 'vorynex-stack').replace(/[^\w-]+/g, '-').toLowerCase() + '.png', { type: 'image/png' });
        const text = `${stackName || S.myStack} — ${link}`;
        await copy(link);                          // for the Instagram link sticker
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text, title: stackName || S.myStack });
          say(S.copied);
        } else {
          const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: file.name });
          document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 4000);
          say(S.saved);
        }
      } catch (e) {
        say(e && e.name === 'AbortError' ? '' : S.shareFailed);
      } finally { shareBtn.removeAttribute('aria-busy'); }
    });
  }

  // ---------- Gift it (buyer): the stack goes to the cart marked as a gift; the buyer keeps the unboxing link ----------
  const giftBox = root.querySelector('[data-vpx-stack-gift]');
  if (giftBox && !gift) {
    const toggle = giftBox.querySelector('[data-vpx-gift-toggle]'), form = giftBox.querySelector('.vpx-stack__gift-form');
    const fields = giftBox.querySelector('[data-vpx-gift-fields]'), done = giftBox.querySelector('[data-vpx-gift-done]');
    const fromIn = giftBox.querySelector('[data-vpx-gift-from]'), toIn = giftBox.querySelector('[data-vpx-gift-to]'), msgIn = giftBox.querySelector('[data-vpx-gift-msg]');
    const linkIn = giftBox.querySelector('[data-vpx-gift-link]'), giftStatus = giftBox.querySelector('[data-vpx-gift-status]');
    const warn = giftBox.querySelector('[data-vpx-gift-cartnote]');
    const addGift = giftBox.querySelector('[data-vpx-gift-add]');
    let giftNoteLine = '';
    const cartCount = () => (cart ? cart.item_count : 0);
    const showWarn = () => { const n = cartCount(); warn.hidden = !n; if (n) warn.textContent = S.giftCartHas.replace('[count]', n); };
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open)); form.hidden = !open;
      if (open) { showWarn(); fromIn.focus(); }
    });
    function giftLink(extra) {
      const url = new URL(location.origin + location.pathname);
      const view = params.get('view'); if (view) url.searchParams.set('view', view);
      if (window.Shopify && Shopify.theme && Shopify.theme.role !== 'main' && Shopify.theme.id) url.searchParams.set('preview_theme_id', Shopify.theme.id);
      const id = extra.id;
      url.searchParams.set('gift', b64u(JSON.stringify({ i: id, f: clean(fromIn.value, 30), t: clean(toIn.value, 30), m: clean(msgIn.value, 180), s: chosen().map(i => i.variant + '.' + i.qty).join(',') })));
      url.searchParams.set('utm_source', 'stack_gift'); url.searchParams.set('utm_medium', 'referral'); url.searchParams.set('utm_campaign', 'build_your_stack');
      return url.toString();
    }
    const newId = () => Array.from(crypto.getRandomValues(new Uint8Array(6)), b => 'abcdefghjkmnpqrstuvwxyz23456789'[b % 31]).join('');
    const namesOk = () => { const ok = clean(fromIn.value, 30) && clean(toIn.value, 30); giftStatus.textContent = ok ? '' : S.giftNeedNames; return ok; };
    giftBox.querySelector('[data-vpx-gift-preview]').addEventListener('click', () => { if (namesOk() && chosen().length) window.open(giftLink({ id: 'preview' }), '_blank', 'noopener'); });
    addGift.addEventListener('click', async () => {
      if (!namesOk() || !chosen().length || addGift.disabled) return;
      addGift.disabled = true; giftStatus.textContent = S.adding;
      const id = newId(), link = giftLink({ id }), from = clean(fromIn.value, 30), to = clean(toIn.value, 30), msg = clean(msgIn.value, 180);
      try {
        const res = await fetch(cartRoot + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: chosen().map(i => ({ id: i.variant, quantity: i.qty })) })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.description || data.message || S.error);
        // The order shows it as a gift: attributes (Admin, emails) and a note line (packing slip).
        giftNoteLine = S.giftNote.replace('[to]', to).replace('[from]', from) + (msg ? ` — "${msg}"` : '');
        const current = await (await fetch(cartRoot + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        const note = [String(current.note || '').trim(), giftNoteLine].filter(Boolean).join('\n');
        const up = await fetch(cartRoot + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note, attributes: { 'Gift': 'Yes', 'Gift id': id, 'Gift from': from, 'Gift to': to, 'Gift message': msg, 'Gift link': link } })
        });
        if (!up.ok) throw new Error(S.error);
        linkIn.value = link;
        done.querySelector('[data-vpx-gift-done-text]').textContent = S.giftAdded.replace('[name]', to) + (cfg.giftEmail ? ' ' + S.giftAddedEmail : '');
        fields.hidden = true; done.hidden = false; giftStatus.textContent = '';
        try { localStorage.setItem('vpx-stack-last-gift', link); } catch (e) { /* private mode */ }
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        openMiniCart();
        readCart();
      } catch (e) {
        giftStatus.textContent = e && e.message && e.message !== 'Failed to fetch' ? e.message : S.error;
      } finally { addGift.disabled = false; }
    });
    giftBox.querySelector('[data-vpx-gift-copy]').addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(linkIn.value); giftStatus.textContent = S.copied; }
      catch (e) { linkIn.select(); giftStatus.textContent = ''; }
    });
    giftBox.querySelector('[data-vpx-gift-undo]').addEventListener('click', async () => {
      try {
        const current = await (await fetch(cartRoot + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        const note = String(current.note || '').split('\n').filter(l => l.trim() && l !== giftNoteLine).join('\n');
        await fetch(cartRoot + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note, attributes: { 'Gift': '', 'Gift id': '', 'Gift from': '', 'Gift to': '', 'Gift message': '', 'Gift link': '' } })
        });
        done.hidden = true; fields.hidden = false; giftStatus.textContent = S.giftUndone;
        readCart();                                   // no longer a gift cart: the monthly option is free again
      } catch (e) { giftStatus.textContent = S.error; }
    });
  }

  // The Story image: 1080 × 1920, the podium with this stack (rendered for this frame), its name, the products
  // and the brand's address. Built from the live scene, so it is exactly what the customer built.
  async function storyImage() {
    const W = 1080, H = 1920, c = document.createElement('canvas'); c.width = W; c.height = H;
    const g = c.getContext('2d');
    await Promise.all(['600 88px "Space Grotesk"', '600 50px "Space Grotesk"', '400 32px Inter', '500 26px "JetBrains Mono"'].map(f => document.fonts.load(f).catch(() => null)));
    g.fillStyle = '#0a0a0b'; g.fillRect(0, 0, W, H);
    const top = 290, sh = 1010;                   // the podium area: the photo covers it exactly, no empty band
    if (three) {
      const shot = await three.snapshot(W, sh);
      const bg = shot.backdrop;
      g.save(); g.beginPath(); g.rect(0, top, W, sh); g.clip();
      g.fillStyle = '#141518'; g.fillRect(0, top, W, sh);
      g.drawImage(backdrop, bg.x, top + bg.y, bg.w, bg.h);
      g.drawImage(shot.canvas, 0, top, W, sh);
      g.restore();
    } else {
      g.drawImage(backdrop, -420, top, 1920, 1072);
    }
    const fade = (y0, y1, from, to) => { const gr = g.createLinearGradient(0, y0, 0, y1); gr.addColorStop(0, from); gr.addColorStop(1, to); g.fillStyle = gr; g.fillRect(0, y0, W, y1 - y0); };
    fade(top, top + 220, 'rgba(10,10,11,1)', 'rgba(10,10,11,0)');
    fade(top + sh - 260, top + sh, 'rgba(10,10,11,0)', 'rgba(10,10,11,1)');
    g.textAlign = 'center'; g.textBaseline = 'alphabetic';
    g.fillStyle = '#ff6b35'; g.font = '500 26px "JetBrains Mono", monospace';
    g.fillText('V O R Y N E X', W / 2, 130);
    g.fillStyle = '#f4f4f5'; g.font = '600 88px "Space Grotesk", sans-serif';
    const title = stackName || S.myStack;
    g.fillText(title.length > 20 ? title.slice(0, 19) + '…' : title, W / 2, 240);
    const list = chosen().map(i => i.name + (i.flavour ? ' ' + i.flavour : '') + (i.qty > 1 ? ' ×' + i.qty : ''));
    g.fillStyle = 'rgba(244,244,245,.78)'; g.font = '400 34px Inter, sans-serif';
    const lines = []; let line = '';
    for (const part of list) { const next = line ? line + '  ·  ' + part : part; if (g.measureText(next).width > W - 140 && line) { lines.push(line); line = part; } else line = next; }
    if (line) lines.push(line);
    lines.slice(0, 3).forEach((l, i) => g.fillText(l, W / 2, top + sh + 90 + i * 54));
    g.fillStyle = 'rgba(244,244,245,.55)'; g.font = '400 30px Inter, sans-serif';
    g.fillText(S.buildYours, W / 2, H - 250);
    g.fillStyle = '#f4f4f5'; g.font = '600 50px "Space Grotesk", sans-serif';
    g.fillText(cfg.site || location.hostname, W / 2, H - 180);
    g.fillStyle = '#ff6b35'; g.font = '500 28px "JetBrains Mono", monospace';
    g.fillText([cfg.instagram ? '@' + cfg.instagram : '', cfg.hashtag ? '#' + cfg.hashtag : ''].filter(Boolean).join('   '), W / 2, H - 112);
    return new Promise(res => c.toBlob(res, 'image/png'));
  }

  rows.forEach(showRow);
  summary();
  readCart();

  // ---------- 3D ----------
  let three = null, threeReady = null, giftMod = null;
  // After the page has loaded — or 2.5 s after this script, whichever comes first: a third-party resource that
  // never finishes (seen on this store) would otherwise hold the load event, and the 3D, forever. A gift starts at
  // once: there the 3D is the page, and its unboxing (vpx-gift.js: the portal, the effects, the sound) loads
  // alongside — only for gift links.
  // (A gift keeps its stack for the unboxing: it lands only when the box is opened — or at once, with no box, when
  // the gift was opened before the 3D arrived.)
  let started = false;
  const start = () => {
    if (started) return; started = true;
    const unboxing = gift ? import(root.dataset.gift).catch(e => { console.error('VORYNEX gift:', e); return null; }) : null;
    threeReady = Promise.all([import(root.dataset.three), unboxing]).then(async ([THREE, G]) => {
      giftMod = G;
      const stage3d = buildStage(THREE);
      await stage3d.ready;   // its programs are compiled in the background first (see buildStage)
      three = stage3d;
      if (!gift) three.sync(true); else if (giftOpened) three.openGift(null, true);
    }).catch(e => console.error('VORYNEX stack 3D:', e));   // the list and the cart still work
  };
  if (gift || document.readyState === 'complete') start();
  else { addEventListener('load', start, { once: true }); setTimeout(start, 2500); }

  function buildStage(THREE) {
    // Shapes: radius of every body 0.82 units; cm = real height (relative size on the podium). Tubs as in the
    // Spotlight; the two bottles traced from their product photos the same way.
    const TUBS = {
      'pre-workout': { cm: 14.9, frontU: 0.17593, height: 2.356,
        parts: [{ mat: 'body', pts: [[0, 0], [0.74, 0], [0.80, 0.02], [0.818, 0.06], [0.82, 0.10], [0.82, 1.975], [0.80, 2.0], [0.772, 2.02], [0.768, 2.04]] },
          { mat: 'lid', pts: [[0, 2.034], [0.79, 2.034], [0.812, 2.048], [0.822, 2.08], [0.822, 2.30], [0.814, 2.336], [0.79, 2.352], [0, 2.356]] }],
        seal: { r: 0.772, y: 2.03, h: 0.03 }, sleeve: { r: 0.8235, bottom: 0.077, top: 1.979 } },
      'creatine': { cm: 14.5, frontU: 0.24995, height: 2.3031,
        parts: [{ mat: 'body', pts: [[0, 0], [0.8011, 0], [0.8011, 0.0045], [0.81, 0.0134], [0.8167, 0.0467], [0.8211, 0.2292], [0.82, 1.8069], [0.8178, 1.9293], [0.8111, 1.9471], [0.7966, 1.9627], [0.7844, 1.9671], [0.7822, 1.9983], [0.8089, 2.0005], [0.8133, 2.0049], [0.8144, 2.0138], [0.81, 2.2608], [0.8011, 2.2809], [0.7833, 2.2875], [0.2848, 2.3031], [0, 2.3031]] }],
        sleeve: { r: 0.8245, bottom: 0.109, top: 1.9115 } },
      'whey-vanilla': { cm: 24.4, frontU: 0.16667, height: 3.211,
        parts: [{ mat: 'body', pts: [[0, 0], [0.8147, 0.0011], [0.8073, 0.0159], [0.8062, 0.205], [0.8115, 0.2135], [0.8126, 0.2262], [0.8211, 2.4781], [0.8147, 2.4951], [0.8126, 2.7564], [0.8083, 2.767], [0.7998, 2.7755], [0.7637, 2.7904], [0.7563, 2.7968], [0.752, 2.8095], [0.751, 2.8414], [0.7616, 2.8541], [0.7956, 2.8626], [0.7956, 2.8796], [0.8062, 2.886], [0.8104, 2.8987], [0.8094, 3.1366], [0.8062, 3.1451], [0.7934, 3.1579], [0.7818, 3.1643], [0.7499, 3.1749], [0.7074, 3.1813], [0.4897, 3.1983], [0.188, 3.211], [0, 3.211]] }],
        sleeve: { r: 0.8245, bottom: 0.0765, top: 2.7213 } },
      'magnesium': { cm: 11.0, frontU: 0.18335, height: 2.8147,
        parts: [{ mat: 'body', pts: [[0, 0], [0.8062, 0.0015], [0.7924, 0.0077], [0.7908, 0.0169], [0.8062, 0.0445], [0.8139, 0.0722], [0.82, 0.1766], [0.8231, 1.9732], [0.8185, 2.093], [0.8108, 2.1299], [0.7878, 2.179], [0.7463, 2.2281], [0.671, 2.2742], [0.4975, 2.3418], [0.4791, 2.3633], [0.476, 2.4738], [0.4822, 2.483], [0.5006, 2.4892], [0.5006, 2.5045], [0.4945, 2.5107], [0.4622, 2.5168]] },
          { mat: 'lid', pts: [[0, 2.523], [0.4622, 2.523], [0.5129, 2.5291], [0.5098, 2.7533], [0.5067, 2.7963], [0.4991, 2.8147], [0, 2.8147]] }],
        sleeve: { r: 0.8245, bottom: 0.0845, top: 1.9809 } },
      // Clear bottle: the whole body carries the unrolled photo (gummies above and below the label), v running
      // with height between bodyTex[0] and bodyTex[1]; glossy plastic, matte paper label.
      'sleep-gummies': { cm: 13.7, bodyTex: [0.004, 2.7784], paper: [0.086, 0.813], frontU: 0.20723, height: 3.2482,
        parts: [{ mat: 'photo', pts: [[0, 0], [0.80, 0], [0.814, 0.008], [0.822, 0.1075], [0.82, 2.3446], [0.814, 2.4401], [0.7882, 2.5157], [0.7404, 2.5834], [0.6667, 2.6351], [0.5573, 2.667], [0.5394, 2.675], [0.5354, 2.6829], [0.5394, 2.7784]] },
          { mat: 'lid', pts: [[0, 2.79], [0.6011, 2.7904], [0.609, 2.7983], [0.6329, 2.8023], [0.6349, 2.8103], [0.6349, 2.8979], [0.6269, 2.9098], [0.6289, 3.1964], [0.619, 3.2163], [0.4737, 3.2482], [0, 3.2482]] }] }
    };
    TUBS['whey-chocolate'] = TUBS['whey-vanilla'];
    // Phones get the 1024 px labels: the products are small there, and six 2048 px labels cost ~70 MB of GPU memory.
    const small = Math.min(screen.width, screen.height) < 700;
    const LABELS = cfg.labels[small ? '1024' : '2048'];

    // Backdrop calibration (vpx-stack-podium.webp, 2752 × 1536): podium Ø 64 cm, top face at y = 0, centre at the
    // origin. Measured on the image: top ellipse centre (1377, 1128), half-width 773 px, front edge 1191, back 1065.
    const IMG = { w: 2752, h: 1536, podiumW: 1547 };
    const PODIUM_R = 32, DROP_H = 100;   // cm; new products fall from above the frame
    const COPY_DX = 0.75;                // a second tub of the same product stands behind, this many radii to the side

    THREE.ShaderChunk.tonemapping_pars_fragment = THREE.ShaderChunk.tonemapping_pars_fragment.replace(
      'vec3 CustomToneMapping( vec3 color ) { return color; }',
      `vec3 CustomToneMapping( vec3 color ) {
        const float StartCompression = 0.8 - 0.04; const float Desaturation = 0.15; color *= toneMappingExposure;
        float x = min( color.r, min( color.g, color.b ) ); float offset = x < 0.08 ? x - 6.25 * x * x : 0.04; color -= offset;
        float peak = max( color.r, max( color.g, color.b ) ); if ( peak < StartCompression ) return color;
        float d = 1. - StartCompression; float newPeak = 1. - d * d / ( peak + d - StartCompression ); color *= newPeak / peak;
        float g = 1. - 1. / ( Desaturation * ( peak - newPeak ) + 1. ); return mix( color, vec3( newPeak ), g ); }`);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, stencil: true, powerPreference: 'high-performance' });
    // Rendered at 1.5–2× even on 1× screens: supersampling smooths the lids' edges and sharpens the small print.
    // (A gift measures the device first and drops to 1× where that is too much: see the probe below.)
    let pixelRatio = Math.min(2, Math.max(1.5, window.devicePixelRatio || 1));
    renderer.setPixelRatio(pixelRatio);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.CustomToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.domElement.setAttribute('aria-hidden', 'true');   // decorative: the list and the summary say it all
    stage.insertBefore(renderer.domElement, backdrop.nextSibling);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(20, IMG.w / IMG.h, 1, 2000);
    camera.position.set(0, 14.69, 183.1); camera.lookAt(0, 14.69, 0);

    // Dark studio matching the photo: a bright softbox above (the light cone), faint vertical strips left and
    // right, and the podium's orange rim as a thin band low in front (kept faint: the environment also lights the
    // white plastic diffusely, and a strong band turned every tub pink).
    // The sky (512×256, equirectangular, row 0 = straight down) is drawn by the GPU into a half-float target, with the
    // formula the CPU used to work out pixel by pixel (~40 ms on a desktop, far more on a slow phone), then filtered by
    // PMREM — once their programs are compiled in the background (see stageReady).
    const pmrem = new THREE.PMREMGenerator(renderer);
    const skyTarget = new THREE.WebGLRenderTarget(512, 256, { type: THREE.HalfFloatType, depthBuffer: false });
    const sky = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.ShaderMaterial({
      vertexShader: 'void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: `
        float easeStep(float e0, float e1, float x) { float t = clamp((x - e0) / (e1 - e0), 0.0, 1.0); return t * t * (3.0 - 2.0 * t); }
        float turn(float a) { return abs(atan(sin(a), cos(a))); }
        void main() {
          float halfTurn = 3.141592653589793;
          float el = (gl_FragCoord.y / 256.0 - 0.5) * halfTurn, az = (gl_FragCoord.x / 512.0 - 0.5) * 2.0 * halfTurn, front = az - halfTurn / 2.0;
          float v = 0.05 + 0.05 * easeStep(-0.2, 0.6, el) + 5.0 * easeStep(0.95, 1.25, el);
          float strips = easeStep(-0.35, -0.15, el) * easeStep(0.7, 0.45, el);
          v += 0.9 * easeStep(0.05, 0.0, turn(front + 1.15)) * strips;
          v += 0.9 * easeStep(0.05, 0.0, turn(front + 0.75)) * strips;
          v += 0.9 * easeStep(0.05, 0.0, turn(front - 0.75)) * strips;
          v += 0.9 * easeStep(0.05, 0.0, turn(front - 1.15)) * strips;
          float rim = 0.5 * easeStep(0.45, 0.0, turn(front)) * easeStep(-0.3, -0.22, el) * easeStep(-0.1, -0.16, el);
          gl_FragColor = vec4(v + rim, v * 0.99 + rim * 0.42, v * 1.02 + rim * 0.2, 1.0);
        }`,
      depthTest: false, depthWrite: false
    }));
    sky.frustumCulled = false;
    const skyCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    // Neutral lights: a front key so the labels read, the photo's cone from above, cool rims against the haze.
    const addLight = (color, intensity, x, y, z) => { const l = new THREE.DirectionalLight(color, intensity); l.position.set(x, y, z); scene.add(l); };
    addLight(0xffffff, 2.2, -50, 70, 120); addLight(0xeef3ff, 1.0, 0, 150, 10); addLight(0xffffff, 0.6, 60, 20, 120);
    addLight(0xdfe8ff, 1.6, -90, 40, -70); addLight(0xffffff, 1.3, 90, 35, -80);

    const labels = labelUploader(THREE, renderer);
    const aniso = 8;   // capped to what the GPU offers at upload (asking at start waited on the busy GPU)
    // ready: the label is on the GPU; failed: it never will be.
    const loadTex = (url, ready, failed) => {
      const t = labels.load(url, () => { ready(); wake(); }, () => { failed(); wake(); });
      t.colorSpace = THREE.SRGBColorSpace; t.wrapS = THREE.RepeatWrapping; t.anisotropy = aniso; return t;
    };
    const MAT = {
      body: new THREE.MeshPhysicalMaterial({ color: 0xffffff, roughness: 0.34, clearcoat: 0.8, clearcoatRoughness: 0.12, envMapIntensity: 0.9 }),
      lid: new THREE.MeshPhysicalMaterial({ color: 0xffffff, roughness: 0.4, clearcoat: 0.6, clearcoatRoughness: 0.16, envMapIntensity: 0.9 }),
      seal: new THREE.MeshStandardMaterial({ color: 0xdedbd8, roughness: 0.5 })
    };
    function paperMask(from, to) {        // clear bottle: 1 = glossy plastic over gummies, dark = matte paper label
      const c = document.createElement('canvas'); c.width = 4; c.height = 256;
      const g = c.getContext('2d', { willReadFrequently: true }); g.fillStyle = '#fff'; g.fillRect(0, 0, 4, 256);
      g.fillStyle = '#1a1a1a'; g.fillRect(0, (1 - to) * 256, 4, (to - from) * 256);
      return new THREE.CanvasTexture(c);
    }
    const built = {};
    function buildTub(key) {
      if (built[key]) return built[key];
      const def = TUBS[key], s = def.cm / def.height, group = new THREE.Group(), inner = new THREE.Group();
      inner.scale.setScalar(s); group.add(inner);
      const entry = { key, group, r: 0, h: def.cm, ready: false, twin: null, maps: [] };
      let waiting = 0;
      const loaded = () => { if (--waiting === 0) { entry.ready = true; wake(); } };
      for (const part of def.parts) {
        const geo = new THREE.LatheGeometry(part.pts.map(([r, y]) => new THREE.Vector2(r, y)), 128);
        let mat = MAT[part.mat];
        if (part.mat === 'photo') {
          const [b, t] = def.bodyTex, pos = geo.attributes.position, uv = geo.attributes.uv;
          for (let i = 0; i < uv.count; i++) uv.setY(i, (pos.getY(i) - b) / (t - b));
          waiting++;
          const map = loadTex(LABELS[key], loaded, loaded); map.offset.x = def.frontU; map.wrapT = THREE.ClampToEdgeWrapping;
          entry.maps.push(map);
          mat = new THREE.MeshPhysicalMaterial({ map, roughness: 0.42, clearcoat: 1, clearcoatMap: paperMask(...def.paper), clearcoatRoughness: 0.06, envMapIntensity: 1 });
        }
        inner.add(new THREE.Mesh(geo, mat));
      }
      if (def.seal) { const m = new THREE.Mesh(new THREE.CylinderGeometry(def.seal.r, def.seal.r, def.seal.h, 128, 1, true), MAT.seal); m.position.y = def.seal.y; inner.add(m); }
      if (def.sleeve) {
        // Until its label has arrived the sleeve stays hidden: a plain tub, never a black band (nor after a failed
        // load).
        const sl = def.sleeve;
        waiting++;
        const map = loadTex(LABELS[key], () => { sleeve.visible = true; loaded(); }, loaded);
        map.offset.x = def.frontU;
        entry.maps.push(map);
        const sleeve = new THREE.Mesh(new THREE.CylinderGeometry(sl.r, sl.r, sl.top - sl.bottom, 160, 1, true),
          new THREE.MeshPhysicalMaterial({ map, roughness: 0.5, clearcoat: 0.35, clearcoatRoughness: 0.25, envMapIntensity: 0.8 }));
        sleeve.position.y = (sl.top + sl.bottom) / 2; sleeve.visible = false; inner.add(sleeve);
      }
      entry.r = Math.max(...def.parts[0].pts.map(p => p[0])) * s;
      if (!waiting) entry.ready = true;
      return (built[key] = entry);
    }

    // Soft contact shadow, one shared texture.
    const sc = document.createElement('canvas'); sc.width = sc.height = 128;
    // A CPU canvas, like every small canvas that only becomes a texture here: drawn by the GPU, its first gradient made
    // the GPU compile its own shaders while the 3D started (a 622 ms flush, measured 2026-10-02 on a cold profile).
    const sg = sc.getContext('2d', { willReadFrequently: true }), rg = sg.createRadialGradient(64, 64, 0, 64, 64, 64);
    rg.addColorStop(0, 'rgba(0,0,0,.9)'); rg.addColorStop(0.36, 'rgba(0,0,0,.62)'); rg.addColorStop(0.62, 'rgba(0,0,0,.18)'); rg.addColorStop(1, 'rgba(0,0,0,0)');
    sg.fillStyle = rg; sg.fillRect(0, 0, 128, 128);
    const shadowTex = new THREE.CanvasTexture(sc);
    const makeShadow = r => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false, toneMapped: false }));
      m.rotation.x = -Math.PI / 2; m.position.y = 0.05; m.scale.set(r * 2.7, r * 2.7, 1); m.renderOrder = 1; return m;
    };

    // Glossy podium: every front tub has a mirrored twin under the top face. It shows only inside the podium's top
    // ellipse (stencil) and fades with depth, so the products stand on the stone instead of looking pasted on.
    const REFLECT = 0.3, FADE = 6.5;       // strength at the base; cm of fade
    const topMask = new THREE.Mesh(new THREE.CircleGeometry(PODIUM_R - 0.8, 96), new THREE.MeshBasicMaterial({
      colorWrite: false, depthWrite: false, stencilWrite: true, stencilRef: 1, stencilFunc: THREE.AlwaysStencilFunc, stencilZPass: THREE.ReplaceStencilOp }));
    topMask.rotation.x = -Math.PI / 2; topMask.renderOrder = -2; scene.add(topMask);
    const mirrorMats = new Map();
    function mirrorMaterial(src) {
      if (mirrorMats.has(src)) return mirrorMats.get(src);
      const m = src.clone();
      Object.assign(m, { transparent: true, depthWrite: false, stencilWrite: true, stencilRef: 1, stencilFunc: THREE.EqualStencilFunc,
        stencilFail: THREE.KeepStencilOp, stencilZFail: THREE.KeepStencilOp, stencilZPass: THREE.KeepStencilOp });
      m.onBeforeCompile = sh => {
        sh.vertexShader = 'varying float vMirrorY;\n' + sh.vertexShader.replace('#include <project_vertex>',
          '#include <project_vertex>\n  vMirrorY = (modelMatrix * vec4(transformed, 1.0)).y;');
        sh.fragmentShader = 'varying float vMirrorY;\n' + sh.fragmentShader.replace('#include <dithering_fragment>',
          `#include <dithering_fragment>\n  gl_FragColor.a *= ${REFLECT.toFixed(3)} * smoothstep(${(-FADE).toFixed(2)}, 0.0, vMirrorY);`);
      };
      m.customProgramCacheKey = () => 'vpx-mirror';
      mirrorMats.set(src, m);
      return m;
    }
    function twinOf(obj) {
      if (!obj.twin) {
        const t = obj.group.clone(true);
        t.traverse(o => { if (o.isMesh) { o.material = mirrorMaterial(o.material); o.renderOrder = -1; } });
        t.scale.y = -1; obj.twin = t;
      }
      return obj.twin;
    }

    const turntable = new THREE.Group(); scene.add(turntable);
    // row key → { holder, tub, obj, shadow, twin, copy, x, z, tx, tz, y, drop, leave, spin, chip }
    const items = new Map();
    const now = () => performance.now();
    const chipsBox = root.querySelector('[data-vpx-stack-chips]');

    // Left-to-right order, kept between changes: a new product goes to the lighter side of the tallest one, next to
    // the first shorter neighbour, and the others only slide sideways — no product ever passes through another.
    // Added tallest-first, this builds a pyramid: tallest in the middle, falling away to both sides.
    let order = [];
    const footprint = it => 2 * it.obj.r + (it.copyWanted ? COPY_DX * it.obj.r : 0);
    const widthOf = arr => arr.reduce((s, x) => s + footprint(x), 0);
    function place(it) {
      if (!order.length) { order.push(it); return; }
      const c = order.indexOf(order.reduce((a, b) => (b.obj.h > a.obj.h ? b : a)));
      if (it.obj.h > order[c].obj.h) {               // a new tallest goes where the row balances best
        let best = 0, bd = Infinity;
        for (let i = 0; i <= order.length; i++) { const d = Math.abs(widthOf(order.slice(0, i)) - widthOf(order.slice(i))); if (d < bd) { bd = d; best = i; } }
        order.splice(best, 0, it); return;
      }
      let i;
      if (widthOf(order.slice(0, c)) <= widthOf(order.slice(c + 1))) { i = c; while (i > 0 && order[i - 1].obj.h >= it.obj.h) i--; }
      else { i = c + 1; while (i < order.length && order[i].obj.h >= it.obj.h) i++; }
      order.splice(i, 0, it);
    }
    function arrange() {
      const gap = 2.4; let x = 0; const xs = [];
      // Each product's footprint runs from −r to +r, plus the copy's overhang on the right when there is one.
      order.forEach((it, i) => { if (i) x += gap; x += it.obj.r; xs.push(x); x += footprint(it) - it.obj.r; });
      order.forEach((it, i) => { it.tx = xs[i] - x / 2; it.tz = -5 + 0.011 * it.tx * it.tx; });   // a shallow arc
      view.kt = zoomFor();
      if (reduceMotion) { view.k = view.kt; applyView(); }
    }
    function add(row, instant) {
      const obj = buildTub(row.current.tub), holder = new THREE.Group(), shadow = makeShadow(obj.r);
      obj.maps.forEach(labels.want);                 // a label still on its way goes first
      holder.add(obj.group, shadow); turntable.add(holder);
      obj.group.rotation.y = 0;
      const chip = document.createElement('span'); chip.className = 'vpx-stack__chip'; chip.hidden = true; chipsBox.appendChild(chip);
      const it = { holder, tub: row.current.tub, obj, shadow, twin: null, copy: null, copyWanted: false, qty: 1, chip,
        x: 0, z: 0, tx: 0, tz: 0, y: instant ? 0 : DROP_H, drop: instant ? null : now(), leave: null, spin: null };
      items.set(row.key, it);
      return it;
    }
    // A second tub of the same product: a clone (same geometry and materials, nothing loaded twice), behind and to
    // the right. More than two appear as a ×n chip: the podium stays readable and light.
    function makeCopy(it, instant) {
      const group = it.obj.group.clone(true), shadow = makeShadow(it.obj.r);
      group.rotation.y = 0;
      const pos = new THREE.Vector3(COPY_DX * it.obj.r, 0, -(2 * it.obj.r + 1.2));
      group.position.copy(pos); shadow.position.set(pos.x, 0.04, pos.z);
      it.holder.add(group, shadow);
      it.copy = { group, shadow, y: instant ? 0 : DROP_H, drop: instant ? null : now(), leave: null };
    }
    function sync(first) {
      const want = new Map(rows.filter(isOn).map(r => [r.key, r]));
      for (const [key, it] of items) if (!want.has(key) && it.leave == null) { it.leave = now(); order = order.filter(x => x !== it); }
      const fresh = [];
      for (const [key, r] of want) {
        let it = items.get(key);
        if (it && it.leave != null) { turntable.remove(it.holder); it.chip.remove(); items.delete(key); it = null; }
        if (!it) { it = add(r, reduceMotion); fresh.push(it); }
        it.qty = r.qty;
        it.copyWanted = r.qty >= 2;
        if (!it.copyWanted && it.copy && it.copy.leave == null) it.copy.leave = now();
      }
      [...fresh].sort((a, b) => b.obj.h - a.obj.h).forEach(place);
      arrange();
      // New arrivals land one after another; on the first load a little slower, as the entrance.
      fresh.forEach((it, i) => { it.x = it.tx; it.z = it.tz; if (it.drop != null) it.drop += i * (first ? 260 : 120) + (first ? 250 : 0); });
      idleAt = now();
      wake();
    }
    function flavour(rowKey) {
      const it = items.get(rowKey), row = rows.find(r => r.key === rowKey);
      if (!it || !row || it.tub === row.current.tub || it.leave != null) return;
      it.spin = { t0: now(), next: row.current.tub };
      buildTub(row.current.tub).maps.forEach(labels.want);   // start loading the new label during the spin
      idleAt = now();
      wake();
    }
    // ---- Stage size and framing: the backdrop box drives the camera's view offset. A zoom around a point just
    // above the podium frames the stack: close for one or two products, wider as it grows.
    const ANCHOR = { x: 1377, y: 1000 };
    // ox/oy: a pan of photo and 3D together (the gift card needs room on the right, or below on phones).
    const view = { W: 1, H: 1, s0: 1, ax: 0, ay: 0, k: 1, kt: 1, ox: 0, oy: 0, oxT: 0, oyT: 0 };
    // On top of it, the unboxing's camera moves: a push-in (k) and a shake (x, y px); neutral otherwise.
    const viewFx = { k: 1, x: 0, y: 0 };
    const wide = matchMedia('(min-width: 901px)');
    function layout(W, H, lift) {
      let s0 = Math.max(W / IMG.w, (H - lift) / IMG.h);   // cover…
      s0 = Math.min(s0, (W * 0.94) / IMG.podiumW);         // …but the whole podium stays in view
      const iw = IMG.w * s0, ih = IMG.h * s0, x0 = (W - iw) / 2, y0 = H - lift - ih;
      return { W, H, s0, x0, y0, iw, ih, ax: x0 + ANCHOR.x * s0, ay: y0 + ANCHOR.y * s0 };
    }
    const RIM_Y = 1352;                               // the podium's orange rim in the photo (px)
    const drawnSize = new THREE.Vector2();
    let introSpace = 0;
    function frame() {
      const W = stage.clientWidth || 1, H = stage.clientHeight || 1;
      // On computers the stage opens under the site header, which then scrolls away while the stage sticks at the
      // top: the podium is framed inside the part visible on arrival, and a dark floor fills the rest below.
      // (Measured on the section, not the stage: a stuck sticky element reports its stuck position.)
      // A gift stands higher, so the words and the button below it sit on the dark floor, clear of the rim.
      let lift;
      if (gift) {
        const intro = root.querySelector('[data-vpx-gift-intro]');
        if (!intro.hidden) introSpace = intro.offsetHeight + (parseFloat(getComputedStyle(intro).bottom) || 0) + 14;
        lift = Math.max(H * 0.18, introSpace - (IMG.h - RIM_Y) * layout(W, H, H * 0.18).s0);
      } else lift = wide.matches ? Math.min(H * 0.3, Math.max(0, root.getBoundingClientRect().top + scrollY)) : 0;
      root.style.setProperty('--vpx-stack-floor', lift + 'px');
      root.classList.toggle('is-lifted', lift > 0);
      const L = layout(W, H, lift);
      root.classList.toggle('has-top-gap', L.y0 > 0.5);   // the photo stops short of the top (tall phones)
      Object.assign(backdrop.style, { left: L.x0 + 'px', top: L.y0 + 'px', width: L.iw + 'px', height: L.ih + 'px', objectFit: 'fill', transformOrigin: `${ANCHOR.x * L.s0}px ${ANCHOR.y * L.s0}px` });
      Object.assign(view, { W, H, s0: L.s0, ax: L.ax, ay: L.ay });
      // Only a real change resizes the canvas: a resize resets its drawing buffer (and at the start it waited for the
      // busy GPU).
      if (renderer.getSize(drawnSize).x !== W || drawnSize.y !== H) renderer.setSize(W, H, false);
      view.kt = view.k = zoomFor();
      if (cardRoom) { roomForCard(); Object.assign(view, { k: view.kt, ox: view.oxT, oy: view.oyT }); }
      applyView();
      wake();
    }
    function applyView() {
      const { W, H, s0, ax, ay } = view, k = view.k * viewFx.k, ox = view.ox + viewFx.x, oy = view.oy + viewFx.y, s = s0 * k;
      backdrop.style.transform = k === 1 && !ox && !oy ? '' : `translate(${ox.toFixed(1)}px, ${oy.toFixed(1)}px) scale(${k.toFixed(4)})`;
      camera.setViewOffset(IMG.w * s, IMG.h * s, -(ax + ox - ANCHOR.x * s), -(ay + oy - ANCHOR.y * s), W, H);
      camera.updateProjectionMatrix();
    }
    function zoomFor(v = view) {
      const list = [...items.values()].filter(it => it.leave == null);
      if (!list.length || v.W <= 1) return 1;
      const left = Math.min(...list.map(it => it.tx - it.obj.r)), right = Math.max(...list.map(it => it.tx - it.obj.r + footprint(it)));
      const width = right - left + 6;
      const tall = Math.max(...list.map(it => it.obj.h));
      const ppc = IMG.podiumW / (2 * PODIUM_R);      // image px per cm on the podium
      const kW = (v.W < 700 ? 0.9 : 0.64) * v.W / (width * ppc * v.s0);
      const kH = (v.ay - 100) / (v.s0 * Math.max(1, tall * ppc - (1128 - ANCHOR.y)));   // the tallest lid stays clear of the top
      const k = Math.max(1, Math.min(1.45, kW, kH));
      return v === view && cardRoom ? Math.min(k, cardRoom.kMax) : k;
    }
    new ResizeObserver(frame).observe(stage);

    // ---- Gift: once the card is up, the scene makes room for it — the whole stack stands in the free space beside
    // the card on computers, above the bottom sheet on phones (smaller, if it must), centred there. Photo and 3D
    // pan together; the numbers follow the calibration: a point (X, Y) of the photo lands at
    // (ax + ox + (X − ANCHOR.x)·s, ay + oy + (Y − ANCHOR.y)·s), s = s0·k, the podium's centre at X ≈ ANCHOR.x.
    const BASE_Y = 1116;                              // where the products stand, in the photo (px)
    let cardRoom = null;                              // { kMax } while the card is up
    function roomForCard() {
      const card = root.querySelector('[data-vpx-gift-card]');
      const list = [...items.values()].filter(it => it.leave == null);
      if (!list.length) return;
      const u = IMG.podiumW / (2 * PODIUM_R) * view.s0;                     // screen px per cm at zoom 1
      const left = Math.min(...list.map(it => it.tx - it.obj.r)), right = Math.max(...list.map(it => it.tx - it.obj.r + footprint(it)));
      const tall = Math.max(...list.map(it => it.obj.h)) + 3;                // + the lid's margin
      cardRoom = { kMax: Infinity };
      if (wide.matches) {
        const free = card.offsetLeft - 48;                                   // 24 px clear on both sides
        cardRoom.kMax = free / ((right - left) * u);
        view.kt = zoomFor();
        view.oxT = 24 + free / 2 - view.ax - (left + right) / 2 * u * view.kt; view.oyT = 0;
      } else {
        const free = card.offsetTop - 36;                                    // 12 px above the sheet, 24 below the top
        cardRoom.kMax = Math.max(0.7, free / (tall * u));
        view.kt = zoomFor();
        const s = view.s0 * view.kt, base = view.ay + (BASE_Y - ANCHOR.y) * s;
        view.oyT = Math.min(0, 24 + (free + tall * u * view.kt) / 2 - base); view.oxT = 0;
      }
    }

    // ---- A still of the stack for the share image: every product settled, facing front, rendered once at the
    // image's size into an offscreen copy; the live canvas is restored in the same task (nothing flickers).
    function snapshot(W, H) {
      for (const it of items.values()) {
        if (it.leave != null) continue;
        it.x = it.tx; it.z = it.tz; it.y = 0; it.drop = null;
        if (it.spin) { it.spin = null; }
        it.obj.group.rotation.y = 0; it.obj.group.position.y = 0; it.holder.position.set(it.x, 0, it.z);
        if (it.twin) { it.twin.position.y = 0; it.twin.rotation.y = 0; }
        if (it.copy) { it.copy.y = 0; it.copy.drop = null; it.copy.group.position.y = 0; }
        it.shadow.material.opacity = 1;
      }
      const angleWas = turntable.rotation.y; turntable.rotation.y = 0;
      const L = layout(W, H, 0), v = { W, H, s0: L.s0, ax: L.ax, ay: L.ay }, k = zoomFor(v), s = L.s0 * k;
      renderer.setPixelRatio(1); renderer.setSize(W, H, false);
      camera.setViewOffset(IMG.w * s, IMG.h * s, -(L.ax - ANCHOR.x * s), -(L.ay - ANCHOR.y * s), W, H);
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
      const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H;
      canvas.getContext('2d').drawImage(renderer.domElement, 0, 0, W, H);
      turntable.rotation.y = angleWas;
      renderer.setPixelRatio(pixelRatio);
      frame();
      renderer.render(scene, camera);
      return { canvas, backdrop: { x: L.ax - ANCHOR.x * s, y: L.ay - ANCHOR.y * s, w: IMG.w * s, h: IMG.h * s } };
    }

    // ---- Turntable: drag with inertia; after a change, a slow sway that settles to the front and stops.
    let angle = 0, vel = 0, dragging = false, lastX = 0, lastT = 0, idleAt = now();
    const SWAY_FOR = 9000;                            // ms of sway after the last change or drag
    const canvas = renderer.domElement;
    canvas.addEventListener('pointerdown', e => { dragging = true; lastX = e.clientX; lastT = now(); vel = 0; canvas.setPointerCapture(e.pointerId); wake(); });
    canvas.addEventListener('pointermove', e => {
      if (!dragging) return;
      const t = now(), dx = e.clientX - lastX;
      angle += dx * 0.008; vel = dx * 0.008 / Math.max(1, t - lastT) * 16.67; lastX = e.clientX; lastT = t; wake();
    });
    const end = () => { if (dragging) { dragging = false; idleAt = now(); wake(); } };
    canvas.addEventListener('pointerup', end); canvas.addEventListener('pointercancel', end);

    const easeInOut = p => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
    // Falls with gravity, lands at 72% of the time, then one small hop (cm).
    const dropY = p => (p < 0.72 ? DROP_H * (1 - (p / 0.72) ** 2) : 1.3 * Math.sin(Math.PI * (p - 0.72) / 0.28));
    const tip = new THREE.Vector3();
    let raf = 0, last = 0, visible = true, relaxed = false, probe = null, live = false;
    // live: nothing is drawn until every program is compiled (see stageReady).
    function wake() { if (live && !raf && visible && !document.hidden) { last = now(); raf = requestAnimationFrame(loop); } }
    function loop(t) {
      raf = 0;
      // A slow, ambient scene (the gift waiting to be opened, or resting after it) is drawn at about 30 fps.
      if (relaxed && !dragging && t - last < 30) { raf = requestAnimationFrame(loop); return; }
      relaxed = false;
      const dt = Math.max(0, Math.min(50, t - last)) / 1000; last = t;
      let busyFrame = dragging;
      if (!dragging) {
        if (Math.abs(vel) > 0.0004) { angle += vel; vel *= Math.pow(0.9, dt * 60); busyFrame = true; }
        else {
          // Back towards the front: a gentle sway for a few seconds after a change, then rest (no frames at rest).
          const since = t - idleAt, swaying = !reduceMotion && since < SWAY_FOR;
          const fade = swaying ? Math.min(1, (SWAY_FOR - since) / 2000) : 0;
          const target = 0.14 * Math.sin(since / 2600) * fade;
          angle += (target - angle) * (1 - Math.exp(-dt * 1.6));
          if (swaying || Math.abs(angle) > 0.0015) busyFrame = true; else angle = 0;
        }
      }
      turntable.rotation.y = angle;
      // The zoom and the pan ease towards their targets and land exactly on them (stopping just short left the framing
      // a hair off, by an amount that depended on the frame timing).
      if (Math.abs(view.kt - view.k) > 0.0004) { view.k += (view.kt - view.k) * (1 - Math.exp(-dt * 3.2)); applyView(); busyFrame = true; }
      else if (view.k !== view.kt) { view.k = view.kt; applyView(); busyFrame = true; }
      if (Math.abs(view.oxT - view.ox) > 0.3 || Math.abs(view.oyT - view.oy) > 0.3) {
        const e = 1 - Math.exp(-dt * 2.6); view.ox += (view.oxT - view.ox) * e; view.oy += (view.oyT - view.oy) * e; applyView(); busyFrame = true;
      } else if (view.ox !== view.oxT || view.oy !== view.oyT) { view.ox = view.oxT; view.oy = view.oyT; applyView(); busyFrame = true; }
      turntable.updateMatrixWorld();
      const k = 1 - Math.exp(-dt * 7);
      for (const [key, it] of items) {
        it.x += (it.tx - it.x) * k; it.z += (it.tz - it.z) * k;
        if (Math.abs(it.tx - it.x) > 0.01 || Math.abs(it.tz - it.z) > 0.01) busyFrame = true;
        if (it.drop != null) {
          const p = Math.min(1, Math.max(0, (t - it.drop) / 900));
          it.y = dropY(p); it.obj.group.rotation.y = -0.7 * (1 - easeInOut(p));
          if (p >= 1) { it.drop = null; it.y = 0; }
          busyFrame = true;
        }
        if (it.leave != null) {
          const p = Math.min(1, (t - it.leave) / 480);
          it.y = DROP_H * 1.1 * p * p;
          if (p >= 1) { turntable.remove(it.holder); it.chip.remove(); items.delete(key); continue; }
          busyFrame = true;
        }
        if (it.spin) {
          const p = Math.min(1, (t - it.spin.t0) / 800);
          it.obj.group.rotation.y = Math.PI * 2 * easeInOut(p);
          if (p >= 0.5 && it.spin.next) {           // swap flavours while the tub shows its side
            const next = buildTub(it.spin.next);
            it.holder.remove(it.obj.group); next.group.rotation.y = it.obj.group.rotation.y; it.holder.add(next.group);
            if (it.twin) { it.holder.remove(it.twin); it.twin = null; }
            if (it.copy) { it.holder.remove(it.copy.group, it.copy.shadow); it.copy = null; }
            it.obj = next; it.tub = it.spin.next; it.spin.next = null;
          }
          if (p >= 1) { it.spin = null; it.obj.group.rotation.y = 0; }
          busyFrame = true;
        }
        // The second tub: lands behind after the first, leaves when the quantity drops back to one.
        if (it.copyWanted && !it.copy && it.obj.ready && it.leave == null && !it.spin) makeCopy(it, reduceMotion || it.drop != null);
        if (it.copy) {
          const cp = it.copy;
          if (cp.drop != null) { const p = Math.min(1, Math.max(0, (t - cp.drop) / 900)); cp.y = dropY(p); if (p >= 1) { cp.drop = null; cp.y = 0; } busyFrame = true; }
          if (cp.leave != null) {
            const p = Math.min(1, (t - cp.leave) / 480); cp.y = DROP_H * 1.1 * p * p;
            if (p >= 1) { it.holder.remove(cp.group, cp.shadow); it.copy = null; } busyFrame = true;
          }
          if (it.copy) { cp.group.position.y = it.y + cp.y; cp.shadow.material.opacity = Math.max(0, 1 - (it.y + cp.y) / 30); }
        }
        it.holder.position.set(it.x, 0, it.z);
        it.obj.group.position.y = it.y;
        if (!it.twin && it.obj.ready) { it.twin = twinOf(it.obj); it.holder.add(it.twin); }
        if (it.twin) { it.twin.position.y = -it.y; it.twin.rotation.y = it.obj.group.rotation.y; }
        it.shadow.material.opacity = Math.max(0, 1 - it.y / 30);
        // ×n chip over the lid for three or more of a product.
        it.chip.hidden = it.qty < 3 || it.leave != null;
        if (!it.chip.hidden) {
          it.chip.textContent = '×' + it.qty;
          tip.set(it.x, it.y + it.obj.h + 1.5, it.z).applyMatrix4(turntable.matrixWorld).project(camera);
          it.chip.style.transform = `translate(${((tip.x + 1) / 2 * view.W).toFixed(1)}px, ${((1 - tip.y) / 2 * view.H).toFixed(1)}px) translate(-50%, -100%)`;
        }
      }
      const g = giftFx ? giftFx.update(t, dt) : 0;     // 1: animating, 2: ambient (30 fps), 0: at rest
      if (g === 1) busyFrame = true;
      if (probe) { busyFrame = true; measure(t); }
      renderer.render(scene, camera);
      if (busyFrame) wake();
      else if (g === 2 && visible && !document.hidden) { relaxed = true; raf = requestAnimationFrame(loop); }
    }
    // A gift measures the device once its scene is ready (about 1.2 s of frames at full speed, before the tap):
    // where frames come slower than ~40 fps, the supersampling goes (1× pixels) and the unboxing stays smooth.
    function measure(t) {
      if (probe.prev) probe.times.push(t - probe.prev);
      probe.prev = t;
      if (t < probe.until) return;
      const times = probe.times.slice(4).sort((a, b) => a - b), median = times[Math.floor(times.length / 2)] || 0;
      probe = null;
      if (median > 24 && pixelRatio > 1) { pixelRatio = 1; renderer.setPixelRatio(1); frame(); }
    }
    new IntersectionObserver(es => { visible = es.some(e => e.isIntersecting); if (visible) wake(); }).observe(stage);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) wake(); });

    // ---- Gift mode (vpx-gift.js): the box on the podium in front of a ring of light; opening it plays the unboxing,
    // with its sound, and the stack lands. Every shader is compiled while the box waits, so the burst never stutters.
    let giftFx = null, compiled = Promise.resolve();
    if (gift && giftMod) {
      giftFx = giftMod.createGift({ THREE, scene, camera, renderer, S, gift, aniso, reduceMotion, PODIUM_R,
        fx: viewFx, applyView, wake, land: () => { sync(false); roomForCard(); },
        afterCompile: fn => compiled.then(fn) });     // what the unboxing frees waits for a compile still running
      root.classList.add('has-gift-box');
    }

    // Every program the stage draws with is compiled in the background before its first frame: compiled at first use,
    // they held the page ~2 s as the 3D started (509 + 623 + 787 + 211 ms, measured 2026-10-02 on a desktop GPU), and
    // a product's first landing later. First the light's programs; then the light itself, which the others depend on;
    // then every product's materials and their reflections, the shadow and, for a gift, its unboxing (warmUp; opened
    // before the 3D arrived, nothing of it is left to play). The canvas gets its size first and the GPU is left to start
    // the context undisturbed (gpuSettled); the labels go up only once the GPU is done with all that. Nothing is drawn
    // until then — the podium photo shows — and the stack lands right after. The first products' labels (or the gift's)
    // start loading at once, ahead of the rest.
    const stageReady = (async () => {
      (gift ? gift.list.map(x => x.item.tub) : rows.filter(isOn).map(r => r.current.tub)).forEach(k => buildTub(k).maps.forEach(labels.want));
      frame();
      await gpuSettled(renderer);
      await lightPrograms(THREE, renderer, pmrem, sky, skyCamera, skyTarget);
      renderer.setRenderTarget(skyTarget);
      renderer.render(sky, skyCamera);
      renderer.setRenderTarget(null);
      scene.environment = pmrem.fromEquirectangular(skyTarget.texture).texture;
      skyTarget.dispose(); sky.geometry.dispose(); sky.material.dispose(); pmrem.dispose();
      const keys = new Set(gift ? gift.list.map(x => x.item.tub) : []);
      rows.forEach(r => r.items.forEach(i => { if (i.available) keys.add(i.tub); }));
      const unboxing = giftFx && !giftOpened;
      await warmUp([...keys], unboxing ? giftFx.objects.filter(o => !o.visible) : [], () => !giftFx || giftFx.state === 'idle');
      await gpuSettled(renderer);
      labels.open();
      live = true;
      root.classList.add('is-3d');
      // A gift measures the device before the tap (see measure).
      if (unboxing && !reduceMotion && giftFx.state === 'idle') probe = { until: now() + 1200, prev: 0, times: [] };
      wake();
    })();
    compiled = stageReady;
    // The programs of these products (their materials, their reflections, their shadow) — and, for a gift, of the
    // unboxing: its hidden effects are shown for a moment. Stand-ins join the scene, compiled (in the background where
    // the browser can), then drawn once into a single pixel, as the first use finishes a program (on Safari it is the
    // compile itself). The real frame is drawn right after, in the same task: nothing flickers. A gift's programs are
    // made while its box waits; still() says whether that draw is still wanted (not once the box is opening).
    function warmUp(keys, hidden = [], still = () => true) {
      const tmp = new THREE.Group(), geo = new THREE.PlaneGeometry(1, 1), seen = new Set();
      keys.forEach(k => buildTub(k).group.traverse(o => {
        if (!o.isMesh || seen.has(o.material)) return;
        seen.add(o.material);
        tmp.add(new THREE.Mesh(geo, o.material), new THREE.Mesh(geo, mirrorMaterial(o.material)));
      }));
      tmp.add(makeShadow(1));
      const stage = on => { if (on) scene.add(tmp); else scene.remove(tmp); hidden.forEach(o => { o.visible = on; }); };
      return quietly(renderer, scene, camera, stage).then(async () => {
        // The stand-ins first, then the products not on the podium, one per task (drawn all at once, the products
        // held the page 20–40 ms), so their shapes are on the graphics card before they land. Each staged draw is
        // followed by the real frame, in the same task: nothing flickers.
        const once = extra => {
          if (extra) tmp.add(extra);
          stage(true);
          renderer.setScissorTest(true); renderer.setScissor(0, 0, 1, 1);
          renderer.render(scene, camera);
          renderer.setScissorTest(false);
          stage(false);
          if (extra) tmp.remove(extra);
          renderer.render(scene, camera);
        };
        if (still()) once(null);
        for (const g of keys.map(k => buildTub(k).group).filter(g => !g.parent)) {
          await new Promise(r => setTimeout(r, 0));
          if (!still()) break;   // a gift opened meanwhile: its frames are drawing
          once(g);
        }
        geo.dispose();
      });
    }
    // sound: { ctx, handle } from the tap (or null). instant: the gift was opened before the 3D arrived.
    function openGift(sound, instant) {
      if (giftFx && giftFx.state !== 'idle') return Promise.resolve();
      const canPlay = !!(sound && giftMod && sound.ctx.state === 'running');
      // No unboxing to play (no motion wanted, no gift module, or opened before the 3D arrived): the lit scene and
      // the stack come straight away, with only the warm chord.
      if (!giftFx || reduceMotion || instant) {
        if (canPlay) sound.handle = giftMod.playReveal(sound.ctx, 0.05, true);
        if (giftFx) giftFx.openNow();
        sync(false);
        roomForCard(); Object.assign(view, { k: view.kt, ox: view.oxT, oy: view.oyT });
        applyView(); wake();
        return Promise.resolve();
      }
      // The sound starts 80 ms from now; the pictures wait that long plus the audio output's own delay (Bluetooth
      // headphones add up to a few hundred ms), so the burst is seen and heard at the same moment.
      let lead = 0;
      if (canPlay) {
        const latency = Math.min(0.3, Math.max(0, (sound.ctx.outputLatency || 0) + (sound.ctx.baseLatency || 0)));
        sound.handle = giftMod.playReveal(sound.ctx, 0.08, false);
        if (!soundOn) sound.handle.setMuted(true);
        lead = (0.08 + latency) * 1000;
      }
      probe = null;
      return giftFx.open(lead);
    }
    frame();                                          // sized now, not on the observer's first call: a gift opened
                                                      // before the 3D arrived pans the scene straight away
    return { ready: stageReady, sync, flavour, openGift, snapshot: (W, H) => Promise.resolve(snapshot(W, H)) };
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Labels on the GPU without stalling the page (the same as in vpx-spotlight.js). Uploaded straight from its image
// inside the image's load event, a 2048 px label held the main thread ~40 ms, decoding included (the Spotlight's
// 4096 px ones 130–180 ms; measured 2026-10-01 on a desktop GPU) — six of them while the first products land. Here the
// browser decodes it off the main thread (createImageBitmap) and it goes to the GPU in strips, one after each frame,
// each sized to take ~4 ms. No WebGL 2, or a label too big for the GPU: the plain image, decoded first and uploaded in
// an idle moment (three.js scales down what the GPU can't hold).
// load(url, ready, failed) returns the texture at once (set its wrap, offset and anisotropy then); `ready` runs when
// the whole label is on the GPU. want(texture) puts that label first in line.
function labelUploader(THREE, renderer) {
  const gl = renderer.getContext(), queue = [], wanted = new Set(), done = [], lost = [];
  let anisotropic = null;   // asked for in open(), when the GPU has nothing else to do
  // Chromium uploads a strip of rows straight from the decoded bitmap (UNPACK_SKIP_ROWS, checked pixel for pixel on
  // 2026-10-01); elsewhere each strip is cut out as a bitmap of its own first, which costs about as much again.
  const direct = !!navigator.userAgentData;
  const afterFrame = () => new Promise(r => requestAnimationFrame(() => setTimeout(r, 0)));
  const idle = fn => (window.requestIdleCallback ? requestIdleCallback(() => fn(), { timeout: 1000 }) : setTimeout(fn, 80));
  const mode = renderer.capabilities.isWebGL2 && typeof createImageBitmap === 'function' ? 'strips' : 'image';
  let busy = false, open;
  // Strips go up only once the stage says so (open): sent while the GPU is still compiling the stage's programs, an
  // upload waited for it (584 ms at the stack's start, measured 2026-10-02). Decoding starts at once.
  const opened = new Promise(r => { open = r; });

  async function run() {
    if (busy) return;
    busy = true;
    while (queue.length) {
      const k = queue.findIndex(j => wanted.has(j.tex)), job = queue.splice(Math.max(0, k), 1)[0];
      try {
        if (mode === 'strips') { try { await strips(job); } catch (e) { if (gl.isContextLost()) throw e; await plain(job); } }
        else await plain(job);
        done.push(job);
        job.ready();
      } catch (e) {
        if (gl.isContextLost()) lost.push(job);                                     // up again once it's back
        else job.failed(e);
      }
    }
    busy = false;
  }
  async function strips(job) {
    const res = await fetch(job.url);
    if (!res.ok) throw new Error('label HTTP ' + res.status);
    // Decoded as it is: asked for any change — flipped (as three.js stores images, bottom row first), with alpha not
    // premultiplied, or without colour management — the browser copied the whole label on the main thread when the
    // bitmap arrived: 60–120 ms a label on a slow phone, 26–40 ms on a desktop (measured 2026-10-02). Untouched, it
    // costs the main thread nothing. The labels are opaque sRGB images (no alpha, no colour profile), so there is
    // nothing to change; the texture is read upside down instead (see below), which costs nothing either.
    const bmp = await createImageBitmap(await res.blob());
    await opened;
    const W = bmp.width, H = bmp.height;
    if (Math.max(W, H) > renderer.capabilities.maxTextureSize) { bmp.close(); throw new Error('label larger than this GPU takes'); }
    const t = gl.createTexture();
    try {
      // A strip's size follows the device: about 4 ms of upload a frame (halved after a slower strip, doubled after a
      // quick one); a label someone is waiting for starts twice as big.
      let bytes = wanted.has(job.tex) ? 1 << 22 : 1 << 21;
      for (let y = 0; y < H;) {
        await afterFrame();
        if (gl.isContextLost()) throw new Error('context lost');
        const rows = Math.min(H - y, Math.max(8, Math.floor(bytes / (W * 4))));
        const strip = direct ? null : await createImageBitmap(bmp, 0, y, W, rows);
        try {
          if (gl.isContextLost()) throw new Error('context lost');
          const began = performance.now();
          renderer.state.bindTexture(gl.TEXTURE_2D, t);   // through three.js's state, so its cache stays true
          if (!y) gl.texStorage2D(gl.TEXTURE_2D, Math.floor(Math.log2(Math.max(W, H))) + 1, gl.SRGB8_ALPHA8, W, H);
          gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false); gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
          if (direct) {
            gl.pixelStorei(gl.UNPACK_SKIP_ROWS, y);
            gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, y, W, rows, gl.RGBA, gl.UNSIGNED_BYTE, bmp);
            gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0);
          } else gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, y, gl.RGBA, gl.UNSIGNED_BYTE, strip);
          const took = performance.now() - began;
          bytes = took > 4 ? Math.max(1 << 18, bytes >> 1) : took < 2 ? Math.min(1 << 22, bytes << 1) : bytes;
        } finally {
          if (strip) strip.close();
        }
        y += rows;
      }
      gl.generateMipmap(gl.TEXTURE_2D);
      // What three.js would set for this texture (sRGB storage above, as three.js picks for an sRGB label).
      const wrap = w => (w === THREE.RepeatWrapping ? gl.REPEAT : w === THREE.MirroredRepeatWrapping ? gl.MIRRORED_REPEAT : gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrap(job.tex.wrapS));
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrap(job.tex.wrapT));
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      if (anisotropic && job.tex.anisotropy > 1) gl.texParameterf(gl.TEXTURE_2D, anisotropic.TEXTURE_MAX_ANISOTROPY_EXT, job.tex.anisotropy);   // capped by the GPU
      // The texture three.js samples is never marked for upload (version 0), so it binds this one as it is — read
      // upside down, as its first row is the label's top.
      renderer.properties.get(job.tex).__webglTexture = t;
      job.tex.repeat.y = -1; job.tex.offset.y = 1;
      job.strips = true;
    } catch (e) {
      gl.deleteTexture(t);
      throw e;
    } finally {
      bmp.close();
    }
  }
  function plain(job) {
    return new Promise((resolve, reject) => {
      new THREE.ImageLoader().load(job.url, img => {
        Promise.resolve(img.decode && img.decode()).catch(() => {}).then(() => opened).then(() => idle(() => {
          job.tex.image = img; job.tex.needsUpdate = true; renderer.initTexture(job.tex); resolve();
        }));
      }, undefined, reject);
    });
  }
  // A lost context comes back empty: three.js uploads its own textures again, the strips are ours to send.
  renderer.domElement.addEventListener('webglcontextrestored', () => {
    queue.push(...lost.splice(0), ...done.filter(j => j.strips));
    done.splice(0, done.length, ...done.filter(j => !j.strips));
    run();
  });

  return {
    load(url, ready, failed) {
      const tex = new THREE.Texture();
      queue.push({ tex, url, ready, failed: failed || (() => {}), strips: false });
      run();
      return tex;
    },
    want(tex) { if (tex) wanted.add(tex); },
    // The stage is ready: uploads may start.
    open() { anisotropic = gl.getExtension('EXT_texture_filter_anisotropic'); open(); }
  };
}

// ---------------------------------------------------------------------------------------------------------------------
// Resolves once the GPU has worked through everything sent to it so far: a fence, polled without blocking. A call that
// needs an answer from the GPU while it is still busy — starting the context, compiling — holds the page until it is
// done (80–600 ms at start, measured 2026-10-02), so the stage asks nothing of it before this.
function gpuSettled(renderer) {
  const gl = renderer.getContext();
  if (!renderer.capabilities.isWebGL2) return new Promise(r => setTimeout(r, 100));
  const sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
  if (!sync) return Promise.resolve();   // a lost context: nothing to wait for
  gl.flush();
  return new Promise(resolve => {
    (function poll() {
      if (gl.clientWaitSync(sync, 0, 0) === gl.TIMEOUT_EXPIRED) { setTimeout(poll, 8); return; }
      gl.deleteSync(sync);
      resolve();
    })();
  });
}

// ---------------------------------------------------------------------------------------------------------------------
// Compiles everything `scene` draws without holding the page. One object's programs per task: three.js writes a
// program's shader text on the main thread, 5–15 ms each on a desktop, and all of them at once held the page 55–80 ms
// (measured 2026-10-02). They are handed to the GPU; the page waits until the GPU has worked through them (gpuSettled)
// and only then, in an idle moment, asks whether they are ready — one program per task: answering, the GPU finishes
// that program's link, ~10–20 ms each (compileAsync asks about all of them at once, and held the page ~50 ms).
// stage(on), if given, puts stand-ins in the scene while it is read.
function idleMoment() { return new Promise(r => (window.requestIdleCallback ? requestIdleCallback(r, { timeout: 1000 }) : setTimeout(r, 50))); }
async function oneByOne(renderer, materials) {
  const gl = renderer.getContext();
  for (const m of materials) {
    const program = renderer.properties.get(m).currentProgram;
    while (program && !program.isReady() && !gl.isContextLost()) await new Promise(r => setTimeout(r, 20));
    await new Promise(r => setTimeout(r, 0));
  }
}
async function quietly(renderer, scene, camera, stage = () => {}) {
  const objects = [], seen = new Set(), materials = new Set();
  stage(true);
  scene.traverse(o => { if (o.material && !seen.has(o.material)) { seen.add(o.material); objects.push(o); } });
  stage(false);
  for (const o of objects) {
    stage(true);
    renderer.compile(o, camera, scene).forEach(m => materials.add(m));   // lit as in the scene
    stage(false);
    await new Promise(r => setTimeout(r, 0));
  }
  await gpuSettled(renderer);
  await idleMoment();
  await oneByOne(renderer, materials);
}

// ---------------------------------------------------------------------------------------------------------------------
// The studio light's programs — the sky drawn into its half-float target, and PMREM's two filters — compiled in the
// background (resolves when they are ready). Each is compiled with the render target it is drawn into bound: that is
// part of a program's identity, and compiled for the screen (as PMREM's own warm-up does in three r160) it would be
// compiled again at first use. PMREM's blur material exists only once its targets are allocated for the size it will
// filter, so that is done here first: the cube a 512 px wide sky gets. Uses PMREMGenerator internals of the pinned
// three r160 (vpx-three.module.min.js).
function lightPrograms(THREE, renderer, pmrem, sky, skyCamera, skyTarget) {
  pmrem._setSize(skyTarget.width / 4);
  pmrem._allocateTargets().dispose();
  pmrem.compileEquirectangularShader();
  const filters = new THREE.Group();
  filters.add(new THREE.Mesh(pmrem._lodPlanes[0], pmrem._blurMaterial), new THREE.Mesh(pmrem._lodPlanes[0], pmrem._equirectMaterial));
  const flat = new THREE.OrthographicCamera(), was = renderer.getRenderTarget();
  const each = how => {
    renderer.setRenderTarget(pmrem._pingPongRenderTarget);
    const filtered = how(filters, flat);
    renderer.setRenderTarget(skyTarget);
    const drawn = how(sky, skyCamera);
    renderer.setRenderTarget(was);
    return [filtered, drawn];
  };
  // Handed to the GPU first, then asked about once it has worked through them (see quietly).
  const [filtered, drawn] = each((o, c) => renderer.compile(o, c));
  return gpuSettled(renderer).then(idleMoment).then(() => oneByOne(renderer, [...filtered, ...drawn])).catch(() => {});
}
